import moment from 'moment';
import Util from '../../../../../forecast-app/shared/util/util';

const formatDateISO = date => (date ? date.format('YYYY-MM-DD') : '');

export function getNextAvailableLock(project) {
	let latestPeriodValue = null;
	// Next lock is either the period after the latest locked period on the project
	// or the period containing the project start date if no locks exist yet.
	if (project.fixedPriceLocks.edges.length > 0) {
		project.fixedPriceLocks.edges.forEach(lock => {
			if (latestPeriodValue == null || latestPeriodValue < lock.node.startDate) {
				latestPeriodValue = lock.node.startDate;
			}
		});
	} else {
		return Util.CreateNonUtcMomentDate(project.projectFirstDateYear, project.projectFirstDateMonth, project.projectFirstDateDay)?.startOf('month');
	}
	return moment(latestPeriodValue).add(1, 'month').startOf('month');
}

export function calculateOptions(project, nextAvailableLock) {
	const options = [];
	if (project && nextAvailableLock) {
		const nextAvailableLockClone = nextAvailableLock.clone();
		const projectLastDate = Util.CreateNonUtcMomentDate(project.projectLastDateYear, project.projectLastDateMonth, project.projectLastDateDay)?.endOf('month');
		const now = moment().startOf('month');
		const lastLockablePeriod = now.isBefore(projectLastDate) ? now : projectLastDate;
		while (nextAvailableLockClone.isBefore(lastLockablePeriod)) {
			options.push({value: formatDateISO(nextAvailableLockClone), label: nextAvailableLockClone.format('MMM YYYY')});
			nextAvailableLockClone.add(1, 'month').startOf('month');
		}
	}
	return options;
}

export function calculatePeriodLockIndexes(columnPeriods, locks) {
	const lockIndexMap = new Map();
	const {firstLockStartDate, lastLockEndDate} = getGlobalLockPeriod(locks);
	if (firstLockStartDate && lastLockEndDate) {
		// Loop over all periods
		for (let i = 0; i < columnPeriods.length; i++) {
			if (columnPeriods[i][0]) {
				const periodStart = moment(columnPeriods[i][0]).startOf('day');
				const periodEnd = moment(columnPeriods[i][1]).endOf('day');

				// Fully locked
				if (firstLockStartDate.isSameOrBefore(periodStart) && lastLockEndDate.isSameOrAfter(periodEnd)) {
					// Find lock for this period index
					locks.forEach(lock => {
						const lockStart = moment(lock.node.startDate).startOf('day');
						const lockEnd = moment(lock.node.endDate).endOf('day');

						// Find the lock that is active for this periods start date
						if (periodStart.isBetween(lockStart, lockEnd, null, '[]')) {
							// Add lock to map and mark it as a full lock
							lockIndexMap.set(i, {...lock.node, fullLock: true});
						}
					});
					// Partially locked
				} else if (periodStart.isBetween(firstLockStartDate, lastLockEndDate, null, '[]') || periodEnd.isBetween(firstLockStartDate, lastLockEndDate, null, '[]')) {
					// Find lock for this period index
					locks.forEach(lock => {
						const lockStart = moment(lock.node.startDate).startOf('day');
						const lockEnd = moment(lock.node.endDate).endOf('day');

						// Find the lock that is active for this periods start date or end date
						if (periodStart.isBetween(lockStart, lockEnd, null, '[]') || periodEnd.isBetween(lockStart, lockEnd, null, '[]')) {
							// Add lock to map and mark it as a partial lock
							lockIndexMap.set(i, {...lock.node, fullLock: false});
						}
					});
				}
			}
		}
	}
	return lockIndexMap;
}

function getGlobalLockPeriod(locks) {
	let firstLockStartDate = null;
	let lastLockEndDate = null;
	if (locks) {
		locks.forEach(lock => {
			if (lock) {
				const lockStart = moment(lock.node.startDate).startOf('day');
				const lockEnd = moment(lock.node.endDate).endOf('day');

				if (firstLockStartDate === null || lockStart.isBefore(firstLockStartDate)) {
					firstLockStartDate = lockStart.clone();
				}

				if (lastLockEndDate === null || lockEnd.isAfter(lastLockEndDate)) {
					lastLockEndDate = lockEnd.clone();
				}
			}
		});
	}

	return {firstLockStartDate, lastLockEndDate};
}

export function getLockedAmountTotal(locks, milestoneBreakdowns) {
	const {firstLockStartDate, lastLockEndDate} = getGlobalLockPeriod(locks);
	if (!firstLockStartDate && !lastLockEndDate) {
		return {lockedWorkTotal: 0, lockedExpenseTotal: 0, lockedOffsetTotal: 0};
	}

	const workBreakdowns = milestoneBreakdowns
		.filter(milestoneBreakdown => milestoneBreakdown.milestoneId !== 'offset')
		.map(milestoneBreakdown => milestoneBreakdown.roleBreakdown)
		.flat()
		.filter(roleBreakdown => roleBreakdown.roleId !== 'expense')
		.map(roleBreakdown => roleBreakdown.breakdown)
		.flat();
	const expenseBreakdowns = milestoneBreakdowns
		.filter(milestoneBreakdown => milestoneBreakdown.milestoneId !== 'offset')
		.map(milestoneBreakdown => milestoneBreakdown.roleBreakdown)
		.flat()
		.filter(roleBreakdown => roleBreakdown.roleId === 'expense')
		.map(roleBreakdown => roleBreakdown.breakdown)
		.flat();
	const offsetBreakdowns = milestoneBreakdowns
		.filter(milestoneBreakdown => milestoneBreakdown.milestoneId === 'offset')
		.map(milestoneBreakdown => milestoneBreakdown.roleBreakdown)
		.flat()
		.map(roleBreakdown => roleBreakdown.breakdown)
		.flat();

	let lockedWorkTotal = 0,
		lockedExpenseTotal = 0,
		lockedOffsetTotal = 0;

	workBreakdowns.forEach(breakdown => {
		const date = Util.CreateUtcMomentDateFromString(breakdown.date);
		if (!date.isBefore(firstLockStartDate) && !date.isAfter(lastLockEndDate)) {
			lockedWorkTotal += breakdown.value;
		}
	});
	expenseBreakdowns.forEach(breakdown => {
		const date = Util.CreateUtcMomentDateFromString(breakdown.date);
		if (!date.isBefore(firstLockStartDate) && !date.isAfter(lastLockEndDate)) {
			lockedExpenseTotal += breakdown.value;
		}
	});
	offsetBreakdowns.forEach(breakdown => {
		const date = Util.CreateUtcMomentDateFromString(breakdown.date);
		if (!date.isBefore(firstLockStartDate) && !date.isAfter(lastLockEndDate)) {
			lockedOffsetTotal += breakdown.value;
		}
	});

	return {lockedWorkTotal, lockedExpenseTotal, lockedOffsetTotal, isUsingLocks: !!locks};
}
